import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

// Same codes as backend/schemas/interventionRating.schema.ts; labels are i18n keys in the components.
// 3 stars or less qualifies what went wrong; 4 or more what was appreciated.
export const RATING_REASON_OTHER = 'other' as const;
export const RATING_REASONS_NEGATIVE = ['appointment_missed', 'not_finished_same_day', 'planogram_mismatch', 'products_misplaced', 'equipment_damaged', 'space_not_restored', RATING_REASON_OTHER] as const;
export const RATING_REASONS_POSITIVE = ['appointment_kept', 'finished_same_day', 'planogram_compliant', 'products_sorted', 'equipment_intact', 'space_left_clean', RATING_REASON_OTHER] as const;
export const NEGATIVE_MAX_SCORE = 3;
export const RATING_DETAIL_MAX_LENGTH = 300;

export type RatingReason = (typeof RATING_REASONS_NEGATIVE)[number] | (typeof RATING_REASONS_POSITIVE)[number];

export interface InterventionRating {
  issueKey: string;
  // Read only as a presence guard (never displayed); id is null when several technicians are named.
  technician: { id: number | null; count: number };
  score: number | null;
  // string[]: pre-nomenclature rows may carry legacy codes; detail is set iff reasons includes 'other'.
  reasons: string[];
  detail: string | null;
  ratedAt: string | null;
  canRate: boolean;
}

export interface RateInterventionBody {
  score: number;
  reasons: RatingReason[];
  detail: string | null;
}

const jsonHeaders = { 'Content-Type': 'application/json' };
const ratingPath = (issueKey: string) => `/issues/${encodeURIComponent(issueKey)}/rating`;

// 403/404 mean "no card to show" for this caller, not an error; so does a payload without a technician.
export const fetchRating = async (issueKey: string): Promise<InterventionRating | null> => {
  const res = await fetchWithAuth(ratingPath(issueKey));
  if (res.status === 403 || res.status === 404) return null;
  const rating = await unwrap<InterventionRating | null>(res, 'Failed to fetch the intervention rating');
  return rating?.technician ? rating : null;
};

export const rateIntervention = async (issueKey: string, body: RateInterventionBody): Promise<InterventionRating> => {
  const res = await fetchWithAuth(ratingPath(issueKey), { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(body) });
  return unwrap<InterventionRating>(res, 'Failed to save the intervention rating');
};

export const deleteRating = async (issueKey: string): Promise<InterventionRating> => {
  const res = await fetchWithAuth(ratingPath(issueKey), { method: 'DELETE' });
  return unwrap<InterventionRating>(res, 'Failed to delete the intervention rating');
};
